import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { UserAccessScope } from '@/pages/stla/useUserAccess';

/**
 * Data access for the user-management screen.
 *
 * `UsersApiController` already had create / get / update / delete / lock /
 * unlock / send-access-email; the paged listing and the bootstrap payload are
 * new, because the Razor page queried `UserManager` straight from its page model
 * and had no endpoint behind it.
 *
 * The grid itself does not go through here — it uses `useServerDataGrid`, which
 * owns the paging state and posts to `POST query`.
 */

const BASE = '/api/users';

export interface UserListItem {
  id: string;
  userName: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  emailConfirmed: boolean;
  lockoutEnabled: boolean;
  lockoutEnd: string | null;
  lastLoginDate: string | null;
  createdDate: string | null;
  roles: string[];
}

export interface UsersBootstrap {
  availableRoles: string[];
  statistics: { active: number; inactive: number; locked: number };
  /**
   * Per role, the access-scope types an account holding it may be given —
   * `UserAccessScopePolicy` server-side. A Dealer User is a dealer-network
   * account and can only be scoped to dealers; an Admin sees everything from the
   * role, so its list is empty and the wizard's scope step has nothing to ask.
   *
   * Read from the server rather than hardcoded here: the create endpoint
   * validates against the same mapping and would reject anything else, so a copy
   * in TypeScript could only ever drift out of agreement with it.
   */
  roleScopes: Record<string, UserAccessScope[]>;
  /**
   * Roles carrying `Permissions.DataScope.Full` — they see everything and need no
   * rule. Separate from an empty `roleScopes` entry, which means the opposite: a
   * role with no data-scope permission at all, which can be given nothing.
   */
  fullAccessRoles: string[];
}

/** One data-visibility rule to create along with the account. */
export interface AccessScopeAssignment {
  scopeType: UserAccessScope;
  /** ISO country code for a Country scope, dealer VAT code for a Dealer one. */
  scopeValue: string;
}

/**
 * `GET /api/users/{id}`. Shaped by `UserDetailsResponse`, which is not quite the
 * list projection: it computes `fullName`, and it reports the lockout as the
 * boolean `isLockedOut` rather than leaving the caller to compare `lockoutEnd`
 * against the clock.
 */
export interface UserDetails extends UserListItem {
  isLockedOut: boolean;
  fullName: string;
  /** The account's rules in `stla_user_access`; empty when it sees nothing. */
  accessScopes: AccessScopeAssignment[];
  /**
   * Multi-factor state, read-only. There is deliberately no administrative
   * "reset MFA" action — a user who loses their authenticator falls back to an
   * emailed code, the product's only recovery path. Do not add one here without
   * revisiting that decision.
   */
  twoFactorEnabled: boolean;
}

export interface CreateUserRequest {
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  /**
   * Rules written to `stla_user_access` with the account, from the wizard's scope
   * step. Optional server-side, where an absent field and an empty list mean the
   * same thing for a brand-new account: no perimeter yet.
   */
  accessScopes?: AccessScopeAssignment[];
}

export interface UpdateUserRequest extends Omit<CreateUserRequest, 'accessScopes'> {
  id: string;
  isActive: boolean;
  emailConfirmed: boolean;
  /**
   * Replaces the account's rules wholesale, so it must be the full set the form
   * loaded and not a delta.
   *
   * Optional, and the distinction matters: omitted leaves the existing rules
   * alone — what the server does with a null, and what every caller written
   * before the scope step sends — while `[]` removes them all. A form that has
   * not managed to read the current rules must omit it rather than send `[]`.
   */
  accessScopes?: AccessScopeAssignment[];
}

/**
 * Whether an account reads as locked, inactive or active.
 *
 * Locked wins over inactive, which is the order the Razor rows rendered their
 * badge in — an account can be both, and the lockout is the thing an
 * administrator acts on.
 */
export type UserStatus = 'locked' | 'inactive' | 'active';

export function userStatus(user: UserListItem): UserStatus {
  if (user.lockoutEnd != null && new Date(user.lockoutEnd) > new Date()) return 'locked';
  if (!user.isActive) return 'inactive';
  return 'active';
}

/** Full name when there is one, otherwise the user name. */
export function userDisplayName(user: UserListItem): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.userName || user.email || user.id;
}

export const userKeys = {
  all: ['admin', 'users'] as const,
  grid: ['admin', 'users', 'grid'] as const,
  bootstrap: ['admin', 'users', 'bootstrap'] as const,
  detail: (id: string) => ['admin', 'users', 'detail', id] as const,
};

/** Assignable roles and the counts behind the tiles above the grid. */
export function useUsersBootstrap() {
  return useQuery<UsersBootstrap>({
    queryKey: userKeys.bootstrap,
    queryFn: () => api.get<UsersBootstrap>(`${BASE}/bootstrap`),
  });
}

/** One user in full, fetched only when a row is opened. */
export function useUserDetails(id: string | null) {
  return useQuery<UserDetails>({
    queryKey: userKeys.detail(id ?? ''),
    queryFn: () => api.get<UserDetails>(`${BASE}/${id}`),
    enabled: id != null,
  });
}

/**
 * Invalidates everything the screen shows after a mutation.
 *
 * Deliberately broad: locking a user changes the row, the status filter it
 * matches and the tiles above the grid, so refreshing only the grid would leave
 * the counts stale.
 */
function useInvalidateUsers() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: userKeys.all });
    // Creating and editing write `stla_user_access` too, which is what the
    // standalone User Access screen lists — leaving its cache alone would show a
    // stale perimeter to anyone who moved between the two screens.
    queryClient.invalidateQueries({ queryKey: ['stla', 'user-access'] });
  };
}

export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (request: CreateUserRequest) => api.post(`${BASE}/create`, request),
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (request: UpdateUserRequest) => api.put(`${BASE}/${request.id}`, request),
    onSuccess: invalidate,
  });
}

export function useDeleteUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: invalidate,
  });
}

export function useLockUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) => api.post(`${BASE}/${id}/lock`),
    onSuccess: invalidate,
  });
}

export function useUnlockUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) => api.post(`${BASE}/${id}/unlock`),
    onSuccess: invalidate,
  });
}

/**
 * Resends the access email — an activation link for an account that has never
 * signed in, a password reset for one that has. The server decides which.
 */
export function useSendAccessEmail() {
  return useMutation({
    mutationFn: (id: string) => api.post(`${BASE}/${id}/send-access-email`),
  });
}
